import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutContent } from '@/mocks/about';
import { siteInfo } from '@/mocks/home';
import { useState, useRef } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9nffbobdkpk99j7giq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed && (parsed as Record<string, { message?: string }>).meta?.message)
          || (parsed && (parsed as Record<string, string>).message)
          || responseText
          || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: 'https://meiguishipin.com',
        description: siteInfo.description,
        foundingDate: '2018',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '科技园南路88号玫瑰大厦18层',
          addressLocality: '深圳市',
          addressRegion: '广东省',
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+86-400-888-6666',
          contactType: 'customer service',
          email: 'contact@meiguishipin.com',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 玫瑰视频',
        url: 'https://meiguishipin.com/about',
        description: '了解玫瑰视频的品牌故事、发展历程、核心价值和联系方式。',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://meiguishipin.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://meiguishipin.com/about' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '玫瑰视频是什么？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '玫瑰视频是一个聚合全网高清影视资源的在线视频平台，提供电视剧全集、电影大全、热播综艺、人气动漫等海量内容。我们致力于为用户提供不缓冲、高清免费的观影体验。',
            },
          },
          {
            '@type': 'Question',
            name: '玫瑰视频需要付费吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '玫瑰视频目前所有内容均可免费在线观看，无需付费。我们通过广告收入来维持平台运营，为用户提供高质量的免费观影服务。',
            },
          },
          {
            '@type': 'Question',
            name: '如何联系玫瑰视频客服？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以通过以下方式联系我们：客服电话 400-888-6666，邮箱 contact@meiguishipin.com，或访问深圳市南山区科技园南路88号玫瑰大厦18层的办公地址。工作时间：周一至周日 9:00-21:00。',
            },
          },
          {
            '@type': 'Question',
            name: '玫瑰视频的内容是否正版？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '玫瑰视频严格遵守国家法律法规，坚持正版内容授权合作。我们与多家版权方建立了合作关系，努力为用户提供合法、优质的视频内容。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <main>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">关于玫瑰视频</h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.slogan}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-5">
                  {aboutContent.brandStory.title}
                </h2>
                {aboutContent.brandStory.paragraphs.map((p, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20office%20interior%20with%20warm%20lighting%20and%20collaborative%20workspace%2C%20clean%20minimalist%20design%2C%20comfortable%20meeting%20area%20with%20plants%20and%20wooden%20furniture%2C%20professional%20atmosphere&width=800&height=500&seq=about-office&orientation=landscape"
                    alt="玫瑰视频办公环境"
                    className="w-full h-48 object-cover rounded-xl"
                    title="玫瑰视频办公环境"
                  />
                </div>
                <div>
                  <div className="bg-background-50 rounded-xl p-4 text-center">
                    <div className="text-xl md:text-2xl font-bold font-heading text-primary-500">{siteInfo.totalUsers}</div>
                    <div className="text-xs text-foreground-500 mt-1">注册用户</div>
                  </div>
                </div>
                <div>
                  <div className="bg-background-50 rounded-xl p-4 text-center">
                    <div className="text-xl md:text-2xl font-bold font-heading text-accent-500">{siteInfo.totalViews}</div>
                    <div className="text-xs text-foreground-500 mt-1">累计播放</div>
                  </div>
                </div>
                <div>
                  <div className="bg-background-50 rounded-xl p-4 text-center">
                    <div className="text-xl md:text-2xl font-bold font-heading text-secondary-500">{siteInfo.totalContent}</div>
                    <div className="text-xs text-foreground-500 mt-1">视频资源</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">核心价值</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutContent.values.map((value, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-5 text-center hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className={`${value.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-6">
                {aboutContent.milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:block flex-1"></div>
                    <div className="absolute left-2.5 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 md:-translate-x-1.5 z-10"></div>
                    <div className="ml-10 md:ml-0 md:flex-1 bg-background-50 rounded-xl p-4">
                      <div className="text-sm font-bold font-heading text-primary-500 mb-1">{m.year}</div>
                      <p className="text-xs md:text-sm text-foreground-600">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutContent.testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <div key={i} className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">公司地址</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{aboutContent.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">联系电话</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{aboutContent.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">工作时间</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{aboutContent.contact.workingHours}</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">在线留言</h3>
                <form className="space-y-3" data-readdy-form onSubmit={handleSubmit} ref={formRef}>
                  <input
                    type="text"
                    placeholder="您的姓名"
                    name="name"
                    required
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-300/60 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="您的邮箱"
                    name="email"
                    required
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-300/60 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                  <textarea
                    placeholder="请输入您的留言（最多500字）..."
                    name="message"
                    rows={3}
                    maxLength={500}
                    required
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-300/60 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="phone_alt"
                    autoComplete="off"
                    tabIndex={-1}
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  {formStatus === 'success' && (
                    <div className="px-3 py-2 text-xs rounded-lg bg-green-50 text-green-700 border border-green-200">
                      留言发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="px-3 py-2 text-xs rounded-lg bg-red-50 text-red-700 border border-red-200">
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}